import { memo, useEffect, useImperativeHandle, useRef } from 'react'
import { LargeContainer, PreviewRoot, innerStyles, smallStyles } from '.'
import { openRoom } from '../../../../actions/room'
import { AppStateType, VideoType } from '../../../../constants/enums'
import { FLOATING_PREVIEW, FloaterSize } from '../../../../constants/floaters'
import { FloaterState } from '../../../../state/modals'
import { closeFloater } from '../../../../state/modals/state.floater'
import { TransitionTypes } from '../../../../utilities/transitions'
import { useAppStateSelector } from '../../../Hooks/useAppState'
import { usePassive } from '../../../Hooks/usePassive'
import { useUserSelector } from '../../../Hooks/useUser'
import OnlineChatHeader from '../../Chat/ChatHeader/OnlineChatHeader'
import BackgroundFade from '../../Chat/Rooms/BackgroundFade'
import LiveSnapStream from '../../LiveSnapStream'
import {
    FloaterModalComponentProps,
    checkParent,
    clickParent,
} from '../Floater'

export enum FloatingPreviewActions {
    video,
    header,
}

const FloatingPreview = memo(
    (props: FloaterState & FloaterModalComponentProps) => {
        const { id, size, location, eventsRef } = props
        const passiveMode = usePassive()
        const videoContainer = useRef<HTMLDivElement>(null)
        const headerContainer = useRef<HTMLDivElement>(null)
        const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
        // const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')

        const width = location.width
        const videoHeight = (width / 16) * 9
        // const foundId = loungeSurrogateID || id
        // const isLounge = !!loungeSurrogateID

        const banned = useUserSelector(id, 'banned')
        const isKicked = useUserSelector(id, 'isKicked')
        const channel = useUserSelector(id, 'channel')
        const isAway = useUserSelector(id, 'isAway')
        const isBroadcasting = useUserSelector(id, 'isBroadcasting')
        const isClubShow = useUserSelector(id, 'isClubShow')
        const isGroupShow = useUserSelector(id, 'isGroupShow')
        const isOffline = useUserSelector(id, 'isOffline')
        const isPrivateShow = useUserSelector(id, 'isPrivateShow')
        const isTruePrivate = useUserSelector(id, 'isTruePrivate')
        const isModel = useUserSelector(id, 'isModel')
        const isNGVideo = useUserSelector(id, 'isNGVideo')
        const phase = useUserSelector(id, 'phase')
        const server = useUserSelector(id, 'server')
        const slug = useUserSelector(id, 'slug')

        useEffect(() => {
            if (!id || banned || isKicked) closeFloater(id, FLOATING_PREVIEW)
        }, [id, banned, isKicked])
        useImperativeHandle(eventsRef, () => {
            return {
                onAction: (event, start, _meta, data) => {
                    if (start) return

                    const target = event.target
                    switch (data) {
                        case FloatingPreviewActions.video:
                            openRoom(id, {
                                // loungeSurrogateID,
                                transitionSource: TransitionTypes.FLOATER,
                            })
                            break
                        case FloatingPreviewActions.header:
                            clickParent(target, 4)
                            break
                        default:
                            break
                    }
                },
                onCheck: (event, start, _metadata) => {
                    if (!start) {
                        const target = event.target as HTMLElement
                        if (checkParent(target, videoContainer.current, 4))
                            return [true, FloatingPreviewActions.video, false]
                        if (checkParent(target, headerContainer.current, 8))
                            return [true, FloatingPreviewActions.header, false]
                    }
                    return [false, null, false]
                },
            }
        }, [id])

        const videoContainerStyles = [innerStyles]
        if (size < FloaterSize.Medium) videoContainerStyles.push(smallStyles)

        return (
            <PreviewRoot
                style={{
                    width: width,
                    height: 'auto',
                }}
                data-testid="PreviewModal__Container"
                id={`floating-preview-${id}`}
            >
                {size >= FloaterSize.Medium && (
                    <LargeContainer
                        ref={headerContainer}
                        data-testid="ChatHeader__Container"
                    >
                        {/* {isLounge ? (
                            <LoungeChatHeader id={id} />
                        ) : ( */}
                        <OnlineChatHeader id={id} preview />
                        {/* )} */}
                    </LargeContainer>
                )}
                <div css={videoContainerStyles} ref={videoContainer}>
                    <BackgroundFade
                        id={id}
                        disabled={passiveMode}
                        width={width}
                        height={(width / 16) * 9}
                        top={24}
                        filter={
                            darkMode
                                ? 'blur(24px) saturate(130%) opacity(48%) brightness(95%)'
                                : 'blur(24px) saturate(130%) opacity(44%) contrast(155%) brightness(95%)'
                        }
                    />
                    <LiveSnapStream
                        id={id}
                        // force re-creation if slug changes
                        key={`floating-preview-snap-stream-${slug}`}
                        videoID={VideoType.FloatingPreview}
                        banned={banned}
                        isAway={isAway}
                        isBroadcasting={isBroadcasting}
                        isClubShow={isClubShow}
                        isGroupShow={isGroupShow}
                        isLounge={false}
                        isModel={isModel}
                        isNGVideo={isNGVideo}
                        isOffline={isOffline}
                        isPrivateShow={isPrivateShow}
                        isTruePrivate={isTruePrivate}
                        noBackground
                        phase={phase}
                        channel={channel}
                        server={server}
                        videoMuted
                        hideControls
                        height={videoHeight}
                        width={width}
                    />
                </div>
            </PreviewRoot>
        )
    },
)

export default FloatingPreview
